import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { toast } from 'sonner';
import {
  useBulkUpdateSellerTags,
  useCreateSellerTagType,
  useCreateSellerTags,
  useDeleteSellerTagType,
  useDeleteSellerTag,
  useReplaceSellerTagAssignments,
  useSellerHierarchies,
  useSellerTagAssignments,
  useSellerTagTypes,
  useUpdateSellerTagType,
  useUpdateSellerTag,
} from '@/lib/query/hooks';
import { apiErrorMessage } from '@/lib/api/client';
import { downloadCSV } from '@/lib/utils';
import type { SellerTagType, SellerTag } from '@/lib/types/feature/seller-tag';
import type { SimpleAccount } from '@/lib/types';
import { TagTypeList } from './TagTypeList';
import { TagPanel } from './TagPanel';
import { TagTypeDialog } from './TagTypeDialog';
import { TagNameDialog } from './TagNameDialog';
import { DeleteTagDialog, type DeleteTarget } from './DeleteTagDialog';
import { SellerTagAssignTable } from './SellerTagAssignTable';
import { TagImportDialog } from './TagImportDialog';
import {
  buildCsvHeaderNames,
  type ImportPlan,
  type ImportSeller,
  type WideImportPlan,
} from './lib/buildImportPlan';
import { strictKey } from './lib/tagNormalize';

interface SellerTagManagementProps {
  account: SimpleAccount;
}

/**
 * 店舗タグ管理タブ（FKV3-13002）
 *
 * 左でタグ種別を選ぶと、その種別のタグ名が右に並ぶ。タグの管理と店舗への付与を
 * 1画面にまとめているのは、CSの作業が「種別を選ぶ → タグ名を確認/追加 → 店舗に付ける」と
 * 連続するため。画面を分けると切り替えるたびに種別を選び直すことになる。
 */
export function SellerTagManagement({ account }: SellerTagManagementProps) {
  const { data, isLoading, error } = useSellerTagTypes(account.code);
  const tagTypes = useMemo(() => data?.tag_types ?? [], [data]);

  // 店舗一覧は店舗階層抽出と同じ Athena のマスタを使う
  const { data: hierarchies = [], isLoading: sellersLoading } = useSellerHierarchies(account.code);
  const { data: assignmentData } = useSellerTagAssignments(account.code);

  // 内部IDを持たない店舗はタグを付けられないので一覧から外す。
  // 残すと行キー・選択キーが空文字で重なって全店舗が1行に潰れ、
  // 全選択すると空の seller_id が混ざって付与リクエストが丸ごと弾かれる
  const sellers: ImportSeller[] = useMemo(
    () =>
      hierarchies
        .filter((h) => h.seller_id)
        .map((h) => ({
          sellerId: h.seller_id ?? '',
          sellerCustomerId: h.seller_customer_id ?? '',
          sellerName: h.seller_name ?? '',
          hierarchy: h.seller_hierarchy ?? '',
        })),
    [hierarchies],
  );

  /** 内部IDが無く、タグを付けられない店舗の件数（黙って消さず画面に出す） */
  const sellersWithoutIdCount = hierarchies.length - sellers.length;

  // 店舗の内部ID → (タグ種別ID → タグ名ID)。1店舗1タグ種別につき1件なので Map で足りる
  const assignments = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    for (const a of assignmentData?.assignments ?? []) {
      const byType = map.get(a.seller_id) ?? new Map<string, string>();
      byType.set(a.tag_type_id, a.tag_id);
      map.set(a.seller_id, byType);
    }
    return map;
  }, [assignmentData]);

  /** タグ種別ID → (店舗の内部ID → タグID)。ワイド形式の差分計算に使う */
  const assignmentsByType = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    for (const [sellerId, byType] of assignments) {
      for (const [typeId, tagId] of byType) {
        const bySeller = map.get(typeId) ?? new Map<string, string>();
        bySeller.set(sellerId, tagId);
        map.set(typeId, bySeller);
      }
    }
    return map;
  }, [assignments]);

  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<SellerTagType | null>(null);
  const [valueDialogOpen, setValueDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<SellerTag | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // 初回表示と、選択中の種別が消えたときに先頭へ寄せる
  useEffect(() => {
    if (tagTypes.length === 0) {
      setSelectedTypeId(null);
      return;
    }
    if (!selectedTypeId || !tagTypes.some((t) => t.id === selectedTypeId)) {
      setSelectedTypeId(tagTypes[0].id);
    }
  }, [tagTypes, selectedTypeId]);

  const selectedType = tagTypes.find((t) => t.id === selectedTypeId) ?? null;

  const createType = useCreateSellerTagType(account.code);
  const updateType = useUpdateSellerTagType(account.code);
  const deleteType = useDeleteSellerTagType(account.code);
  const createValues = useCreateSellerTags(account.code);
  const updateValue = useUpdateSellerTag(account.code);
  const deleteValue = useDeleteSellerTag(account.code);
  const bulkUpdate = useBulkUpdateSellerTags(account.code);
  const replaceAssignments = useReplaceSellerTagAssignments(account.code);

  const handleSelectType = (tagTypeId: string) => setSelectedTypeId(tagTypeId);

  const handleSubmitType = async (name: string, values: string[]) => {
    try {
      if (editingType) {
        await updateType.mutateAsync({ tagTypeId: editingType.id, request: { name } });
        toast.success(`タグ種別の名前を「${name}」に変更しました`);
      } else {
        await createType.mutateAsync({ name, tags: values });
        toast.success(
          values.length > 0
            ? `タグ種別「${name}」をタグ名${values.length}件とあわせて作成しました`
            : `タグ種別「${name}」を作成しました`,
        );
      }
      setTypeDialogOpen(false);
      setEditingType(null);
    } catch (e) {
      toast.error(
        apiErrorMessage(
          e,
          editingType ? 'タグ種別の名前の変更に失敗しました' : 'タグ種別の作成に失敗しました',
        ),
      );
    }
  };

  const handleSubmitValues = async (names: string[]) => {
    if (!selectedType) return;
    try {
      if (editingValue) {
        await updateValue.mutateAsync({
          tagId: editingValue.id,
          request: { name: names[0] },
        });
        toast.success(`タグ名を「${names[0]}」に変更しました`);
      } else {
        const result = await createValues.mutateAsync({
          tagTypeId: selectedType.id,
          request: { names },
        });
        const created = result.created.length;
        const skipped = result.skipped.length;
        toast.success(
          skipped > 0
            ? `タグ名を${created}件追加しました（${skipped}件は既にあるため追加しませんでした）`
            : `タグ名を${created}件追加しました`,
        );
      }
      setValueDialogOpen(false);
      setEditingValue(null);
    } catch (e) {
      toast.error(
        apiErrorMessage(
          e,
          editingValue ? 'タグ名の変更に失敗しました' : 'タグ名の追加に失敗しました',
        ),
      );
    }
  };

  /** 選択中のタグ種別の付与（店舗の内部ID → タグ名ID） */
  const currentAssignments = useMemo(() => {
    const map = new Map<string, string>();
    if (!selectedTypeId) return map;
    for (const [sellerId, byType] of assignments) {
      const valueId = byType.get(selectedTypeId);
      if (valueId) map.set(sellerId, valueId);
    }
    return map;
  }, [assignments, selectedTypeId]);

  const handleAssign = async (sellerIds: string[], tagId: string) => {
    try {
      const result = await bulkUpdate.mutateAsync({
        assign: sellerIds.map((seller_id) => ({
          seller_id,
          tag_id: tagId,
        })),
      });
      toast.success(`${result.assigned}店舗のタグを設定しました`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'タグの設定に失敗しました'));
    }
  };

  const handleUnassign = async (sellerIds: string[]) => {
    // 剥奪はタグ名IDの指定が要るので、いま付いているものを引く
    const unassign = sellerIds
      .map((seller_id) => ({
        seller_id,
        tag_id: currentAssignments.get(seller_id) ?? '',
      }))
      .filter((i) => i.tag_id);
    if (unassign.length === 0) {
      toast.info('選択した店舗にはタグが付いていません');
      return;
    }
    try {
      const result = await bulkUpdate.mutateAsync({ unassign });
      toast.success(`${result.unassigned}店舗のタグを外しました`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'タグの解除に失敗しました'));
    }
  };

  /**
   * 雛形を落とす（タグ種別が列に並ぶワイド形式）。
   *
   * 1種別ずつの雛形は用意しない。全種別が1枚に載っていれば足りるうえ、
   * 2種類あると「どちらを使うのか」を利用者に考えさせることになるため。
   * 列構成は店舗階層抽出タブのCSVと同じなので、そちらを編集して貼り戻してもよい。
   */
  const handleDownloadTemplate = () => {
    // 種別名が固定列と衝突すると列を上書きしてしまうので、取り込みと同じ規則で逃がす
    const headers = buildCsvHeaderNames(tagTypes.map((t) => t.name));
    const rows = sellers.map((seller) => {
      const row: Record<string, string> = {
        店舗ID: seller.sellerCustomerId,
        店舗名: seller.sellerName,
        店舗階層: seller.hierarchy,
      };
      tagTypes.forEach((type, i) => {
        const tagId = assignmentsByType.get(type.id)?.get(seller.sellerId);
        row[headers[i]] = type.tags.find((t) => t.id === tagId)?.name ?? '';
      });
      return row;
    });
    const today = new Date().toISOString().split('T')[0];
    downloadCSV(rows, `店舗タグ_全種別_${account.code}_${today}.csv`);
  };

  /**
   * 複数タグ種別をまとめて登録する（ワイド形式）。
   *
   * タグ種別ごとに「不足タグの作成 → 紐づけ」を順に行う。種別をまたぐ一括APIは
   * 持たないので、1種別ずつ既存のエンドポイントを呼ぶ。
   * 途中で失敗したらそこで止め、どこまで反映されたかを伝える（黙って部分適用にしない）。
   */
  const handleImportWide = async (
    widePlan: WideImportPlan,
    replaceMode: boolean,
    createMissingTypes: boolean,
  ) => {
    // 種別を作らない設定なら、CSVにしか無い列は対象から外す
    const targets = widePlan.entries.filter((e) => e.tagTypeId || createMissingTypes);
    if (targets.length === 0) {
      toast.info('登録できるタグ種別がありません');
      return;
    }

    let doneTypes = 0;
    let totalAssigned = 0;
    let totalUnassigned = 0;
    let createdTypes = 0;
    try {
      for (const target of targets) {
        const tagNames = Array.from(new Set(target.plan.entries.map((e) => e.tagName)));
        let tagTypeId = target.tagTypeId;
        // 種別ID → タグ名 → タグID を引くための索引
        const nameToId = new Map<string, string>();

        if (!tagTypeId) {
          // 新しい種別は、タグ名も同時に作る（API が種別とタグの同時作成に対応している）
          const created = await createType.mutateAsync({
            name: target.tagTypeName,
            tags: tagNames,
          });
          const createdType = created.tag_types.find(
            (t) => strictKey(t.name) === strictKey(target.tagTypeName),
          );
          if (!createdType) {
            throw new Error(`タグ種別「${target.tagTypeName}」を作成できませんでした`);
          }
          tagTypeId = createdType.id;
          createdTypes++;
          for (const tag of createdType.tags) nameToId.set(strictKey(tag.name), tag.id);
        } else {
          const existing = tagTypes.find((t) => t.id === tagTypeId);
          for (const tag of existing?.tags ?? []) nameToId.set(strictKey(tag.name), tag.id);
          const missing = tagNames.filter((name) => !nameToId.has(strictKey(name)));
          if (missing.length > 0) {
            const created = await createValues.mutateAsync({
              tagTypeId,
              request: { names: missing },
            });
            for (const tag of created.created) nameToId.set(strictKey(tag.name), tag.id);
            // 既存と衝突したものは、返ってきた既存IDを使う
            for (const skipped of created.skipped) {
              nameToId.set(strictKey(skipped.name), skipped.existing_id);
              nameToId.set(strictKey(skipped.existing_name), skipped.existing_id);
            }
          }
        }

        const assignInputs = target.plan.entries
          .map((e) => ({ seller_id: e.sellerId, tag_id: nameToId.get(strictKey(e.tagName)) ?? '' }))
          .filter((i) => i.tag_id);

        // 洗い替えは種別ごとに行う。対象が空でも「CSVに無い店舗を外す」ために呼ぶ
        if (replaceMode) {
          const result = await replaceAssignments.mutateAsync({
            tagTypeId,
            request: { assignments: assignInputs },
          });
          totalAssigned += result.assigned;
          totalUnassigned += result.unassigned;
        } else if (assignInputs.length > 0) {
          const result = await bulkUpdate.mutateAsync({ assign: assignInputs });
          totalAssigned += result.assigned;
        }
        doneTypes++;
      }

      setImportOpen(false);
      const createdMessage = createdTypes > 0 ? `タグ種別を${createdTypes}件作成し、` : '';
      const unassignedMessage =
        totalUnassigned > 0 ? `（${totalUnassigned}件のタグを外しました）` : '';
      toast.success(
        `${createdMessage}${doneTypes}種別・${totalAssigned}店舗のタグを設定しました${unassignedMessage}`,
      );
    } catch (e) {
      // どこまで反映されたかを伝える。やり直すと既に入ったぶんは「変更なし」になる
      const progress = doneTypes > 0 ? `（${doneTypes}種別まで反映済み）` : '';
      toast.error(`${apiErrorMessage(e, 'タグの登録に失敗しました')}${progress}`);
    }
  };

  /**
   * 登録を確定する（1タグ種別ぶん）。
   *
   * 先に足りないタグ名を作ってから付与する。作成APIは既存と衝突したものを
   * skipped として既存IDごと返すので、並行して同じ名前が作られていても続行できる。
   */
  const handleImport = async (entries: ImportPlan['entries'], replaceMode: boolean) => {
    if (!selectedType) return;
    try {
      const nameToId = new Map(selectedType.tags.map((v) => [strictKey(v.name), v.id]));
      const missing = Array.from(
        new Set(entries.map((e) => e.tagName).filter((name) => !nameToId.has(strictKey(name)))),
      );

      if (missing.length > 0) {
        const created = await createValues.mutateAsync({
          tagTypeId: selectedType.id,
          request: { names: missing },
        });
        for (const value of created.created) nameToId.set(strictKey(value.name), value.id);
        for (const skipped of created.skipped) {
          nameToId.set(strictKey(skipped.name), skipped.existing_id);
          nameToId.set(strictKey(skipped.existing_name), skipped.existing_id);
        }
      }

      const assignInputs = entries
        .map((e) => ({
          seller_id: e.sellerId,
          tag_id: nameToId.get(strictKey(e.tagName)) ?? '',
        }))
        .filter((i) => i.tag_id);

      const result = replaceMode
        ? await replaceAssignments.mutateAsync({
            tagTypeId: selectedType.id,
            request: { assignments: assignInputs },
          })
        : await bulkUpdate.mutateAsync({ assign: assignInputs });

      setImportOpen(false);
      const createdMessage = missing.length > 0 ? `タグ名を${missing.length}件作成し、` : '';
      const unassignedMessage =
        replaceMode && result.unassigned > 0
          ? `（${result.unassigned}店舗のタグを外しました）`
          : '';
      toast.success(
        `${createdMessage}${result.assigned}店舗のタグを設定しました${unassignedMessage}`,
      );
    } catch (e) {
      toast.error(apiErrorMessage(e, 'タグの登録に失敗しました'));
    }
  };

  const handleConfirmDelete = async (target: DeleteTarget) => {
    // 付与やタグ名が残っていても消すことを、確認ダイアログで承諾済み
    const cascade = target.sellerCount > 0 || (target.valueCount ?? 0) > 0;
    try {
      if (target.kind === 'type') {
        await deleteType.mutateAsync({ tagTypeId: target.id, cascade });
        toast.success(`タグ種別「${target.name}」を削除しました`);
      } else {
        await deleteValue.mutateAsync({ tagId: target.id, cascade });
        toast.success(`タグ名「${target.name}」を削除しました`);
      }
      setDeleteTarget(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, '削除に失敗しました'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>店舗タグを読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {apiErrorMessage(error, '店舗タグの読み込みに失敗しました。')}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>
          店舗タグ管理
        </Typography>
        <Typography variant="body2" color="text.secondary">
          店舗マスタの階層とは別に、店舗を分ける軸を自由に持てます。右でタグ種別を選ぶと、その種別が一覧の編集対象になります。1店舗に付けられるのは、1つの種別につき1つのタグ名だけです。
        </Typography>
      </Box>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          {sellersWithoutIdCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              店舗IDを取得できない店舗が{sellersWithoutIdCount}件あり、一覧に表示していません。
              これらの店舗にはタグを付けられません。
            </Alert>
          )}
          {!selectedType ? (
            <Alert severity="info">
              右の「タグ種別」から種別を作成すると、ここに店舗一覧が表示されます。
            </Alert>
          ) : sellersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }} variant="body2">
                店舗一覧を読み込み中...
              </Typography>
            </Box>
          ) : (
            <SellerTagAssignTable
              sellers={sellers}
              tagTypes={tagTypes}
              selectedTypeId={selectedType.id}
              assignments={assignments}
              onAssign={handleAssign}
              onUnassign={handleUnassign}
              onOpenImport={() => setImportOpen(true)}
              submitting={bulkUpdate.isPending}
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          <TagTypeList
            tagTypes={tagTypes}
            selectedTypeId={selectedTypeId}
            onSelect={handleSelectType}
            onAdd={() => {
              setEditingType(null);
              setTypeDialogOpen(true);
            }}
            onRename={(tagType) => {
              setEditingType(tagType);
              setTypeDialogOpen(true);
            }}
            onDelete={(tagType) =>
              setDeleteTarget({
                kind: 'type',
                id: tagType.id,
                name: tagType.name,
                valueCount: tagType.tags.length,
                sellerCount: tagType.tags.reduce((sum, v) => sum + v.seller_count, 0),
              })
            }
          />

          {selectedType && (
            <TagPanel
              tagType={selectedType}
              onAddValues={() => {
                setEditingValue(null);
                setValueDialogOpen(true);
              }}
              onRenameValue={(value) => {
                setEditingValue(value);
                setValueDialogOpen(true);
              }}
              onDeleteValue={(value) =>
                setDeleteTarget({
                  kind: 'value',
                  id: value.id,
                  name: value.name,
                  sellerCount: value.seller_count,
                })
              }
            />
          )}
        </div>
      </div>

      <TagTypeDialog
        open={typeDialogOpen}
        onOpenChange={(open) => {
          setTypeDialogOpen(open);
          if (!open) setEditingType(null);
        }}
        existingTypeNames={tagTypes.map((t) => t.name)}
        editingName={editingType?.name}
        submitting={createType.isPending || updateType.isPending}
        onSubmit={handleSubmitType}
      />

      {selectedType && (
        <TagNameDialog
          open={valueDialogOpen}
          onOpenChange={(open) => {
            setValueDialogOpen(open);
            if (!open) setEditingValue(null);
          }}
          tagTypeName={selectedType.name}
          existingNames={selectedType.tags.map((v) => v.name)}
          editingName={editingValue?.name}
          submitting={createValues.isPending || updateValue.isPending}
          onSubmit={handleSubmitValues}
        />
      )}

      {selectedType && (
        <TagImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          tagType={selectedType}
          sellers={sellers}
          currentAssignments={currentAssignments}
          allTagTypes={tagTypes}
          assignmentsByType={assignmentsByType}
          submitting={
            createValues.isPending ||
            bulkUpdate.isPending ||
            replaceAssignments.isPending ||
            createType.isPending
          }
          onDownloadTemplate={handleDownloadTemplate}
          onSubmit={handleImport}
          onSubmitWide={handleImportWide}
        />
      )}

      <DeleteTagDialog
        target={deleteTarget}
        submitting={deleteType.isPending || deleteValue.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}
